/**
 * Seiten- und Sortierschluessel sind kein Filter — wer die Liste umsortiert oder nachlaedt, aendert
 * nicht, WELCHE Zeilen sie zeigt. Ohne diese Ausnahme verloere die aktive Kachel ihre Markierung
 * beim ersten Sortierklick, und "Alle Patient:innen" waere nie aktiv (der Default-Filter dieser
 * Seite traegt sortField/sortOrder).
 */
const NON_FILTER_KEYS = new Set(['pageSize', 'continuationToken', 'sortField', 'sortOrder'])

/**
 * Vergleicht zwei Dashboard-Filter fachlich, damit eine KPI-Kachel weiss, ob GENAU ihr Filter
 * gerade auf der Liste liegt.
 *
 * Bewusst kein `JSON.stringify`-Vergleich: die Schluesselreihenfolge haengt davon ab, wie der
 * Filter entstanden ist (Kachel-Klick vs. Seitenleiste), und `undefined` faellt beim Serialisieren
 * still weg. Hier gilt `undefined`, `null` und die leere Liste gleichermassen als "nicht gesetzt".
 *
 * Verschachtelte Objekte kennt der Vergleich nicht (die Filter-DTOs sind flach) — taucht eines auf,
 * gilt die Kachel schlicht als nicht aktiv. Der Fehler faellt damit zur sicheren Seite.
 */
export function dashboardFiltersEqual(a: object, b: object): boolean {
  // Beide Seiten auf heute ausrechnen: eine Kachel „Letzter Monat" von gestern ist dieselbe wie die
  // von heute, auch wenn ihre gespeicherten Daten inzwischen einen Monat zurueckliegen.
  const left = mitAktuellenZeitraeumen(a) as Record<string, unknown>
  const right = mitAktuellenZeitraeumen(b) as Record<string, unknown>

  return [...new Set([...Object.keys(left), ...Object.keys(right)])]
    .filter((key) => !NON_FILTER_KEYS.has(key))
    .every((key) => filterValuesEqual(left[key], right[key]))
}

function filterValuesEqual(a: unknown, b: unknown): boolean {
  if (isUnset(a) && isUnset(b)) {
    return true
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((entry, index) => entry === b[index])
  }
  return a === b
}

function isUnset(value: unknown): boolean {
  return value === undefined || value === null || (Array.isArray(value) && value.length === 0)
}

/**
 * Die beiden Grenzen eines Datumsfilters, der serverseitig gegen einen ZEITSTEMPEL vergleicht
 * (`createdAt`, `ekvRequestDate`). Materials Datumsfeld liefert Mitternacht; ohne `tagesEnde` waere
 * der als „bis" gewaehlte Tag damit selbst ausgeschlossen, und wer „bis heute" waehlt, saehe
 * ausgerechnet die Vorgaenge von heute nicht. Genau so stand es bis 09/2026 in allen
 * Dashboard-Filtern (die Patientenliste kam als letzte dran, Guard in der Spec) — der Fehler meldet sich nicht, die Liste ist einfach kuerzer.
 *
 * <p>Gerechnet wird in UTC, weil der Datepicker in UTC anzeigt (`MAT_MOMENT_DATE_ADAPTER_OPTIONS`
 * mit `useUtc` in `app.config.ts`). Eine lokal gesetzte Grenze stuende im Feld sonst als Vortag.
 *
 * <p>`valueOf()` statt `getTime()`, weil hier ein Moment ankommt und kein `Date` — die Typangabe
 * `Date` ist die des Datepicker-Eingangs, das Objekt kommt aus dem Moment-Adapter. Beide Klassen
 * beantworten `valueOf()` mit den Millisekunden. Wer selbst ein Datum baut, nimmt die Mittagsstunde
 * (`new Date(jahr, monat, tag, 12)`): ein lokal erzeugtes Mitternachtsdatum liegt im Sommer bereits
 * im UTC-Vortag.
 */
export function tagesBeginn(date: Date): string {
  const tag = new Date(date.valueOf())
  return new Date(Date.UTC(tag.getUTCFullYear(), tag.getUTCMonth(), tag.getUTCDate())).toISOString()
}

export function tagesEnde(date: Date): string {
  const tag = new Date(date.valueOf())
  return new Date(Date.UTC(tag.getUTCFullYear(), tag.getUTCMonth(), tag.getUTCDate(), 23, 59, 59, 999)).toISOString()
}

/**
 * Ein Tag relativ zu heute, fuer die Schnellbereiche der Filter („Letzte 7 Tage"). Mittags gebaut,
 * damit `tagesBeginn`/`tagesEnde` denselben Kalendertag sehen — `setDate(getDate() - 7)` auf einem
 * lokalen Mitternachtsdatum landet im Sommer im UTC-Vortag.
 */
export function tagZurueck(bezug: Date, tage: number): Date {
  return new Date(bezug.getFullYear(), bezug.getMonth(), bezug.getDate() - tage, 12)
}

/**
 * Die Schnellbereiche der Filter („Letzter Monat", „Letzte 7 Tage"). Ein so gewaehlter Zeitraum
 * LAEUFT MIT: gespeichert wird die Marke, nicht die beiden Daten, die sie heute ergibt.
 *
 * <p>Anlass (09/2026): ein Kunde hatte sich eine Schnellfilter-Kachel „Letzter Monat"
 * angelegt, gespeichert waren aber die festen Grenzen vom Tag des Anlegens (Mai). Die Kachel zeigte
 * damit fuer immer den Mai, obwohl ihr Titel etwas anderes sagt — und nichts wies darauf hin.
 */
export type DashboardZeitraum = 'last7Days' | 'last14Days' | 'last21Days' | 'thisMonth' | 'lastMonth'

export const DASHBOARD_ZEITRAUM_LABEL: Record<DashboardZeitraum, string> = {
  last7Days: 'Letzte 7 Tage',
  last14Days: 'Letzte 14 Tage',
  last21Days: 'Letzte 21 Tage',
  thisMonth: 'Dieser Monat',
  lastMonth: 'Letzter Monat',
}

/**
 * Die Marken, die ein Dashboard-Filter neben seinen Datumsfeldern tragen kann. Reines
 * Frontend-Wissen: `DashboardService` nimmt sie vor dem Senden heraus (`fuerBackend`).
 * Eine Marke gilt nur, solange die Daten aus ihr stammen — wer ein Datum von Hand aendert,
 * loescht sie.
 */
export interface DashboardZeitraeume {
  /** Bezieht sich auf `fromCreatedDate` / `toCreatedDate`. */
  createdZeitraum?: DashboardZeitraum
  /** Bezieht sich auf `ekvRequestFromDate` / `ekvRequestUntilDate`. */
  ekvRequestZeitraum?: DashboardZeitraum
}

/** Die Grenzen, die ein Schnellbereich HEUTE ergibt — dieselbe Rechnung, die vorher in jedem Filter stand. */
export function zeitraumGrenzen(zeitraum: DashboardZeitraum, heute: Date = new Date()): { von: string; bis: string } {
  const von = ((): Date => {
    switch (zeitraum) {
      case 'last7Days':
        return tagZurueck(heute, 7)
      case 'last14Days':
        return tagZurueck(heute, 14)
      case 'last21Days':
        return tagZurueck(heute, 21)
      case 'thisMonth':
        return new Date(heute.getFullYear(), heute.getMonth(), 1, 12)
      case 'lastMonth':
        return new Date(heute.getFullYear(), heute.getMonth() - 1, 1, 12)
    }
  })()
  // Der letzte Tag des Vormonats ist der „nullte" des laufenden.
  const bis = zeitraum === 'lastMonth' ? new Date(heute.getFullYear(), heute.getMonth(), 0, 12) : tagZurueck(heute, 0)
  return { von: tagesBeginn(von), bis: tagesEnde(bis) }
}

/**
 * Rechnet die Datumsfelder eines Filters aus seinen Marken neu aus. Ohne Marke bleibt ein Datum
 * unangetastet: ein von Hand gewaehlter Zeitraum ist fest und soll es bleiben.
 */
export function mitAktuellenZeitraeumen<T extends object>(filter: T, heute: Date = new Date()): T {
  const { createdZeitraum, ekvRequestZeitraum } = filter as DashboardZeitraeume
  let ergebnis: T = filter
  if (createdZeitraum) {
    const { von, bis } = zeitraumGrenzen(createdZeitraum, heute)
    ergebnis = { ...ergebnis, fromCreatedDate: von, toCreatedDate: bis }
  }
  if (ekvRequestZeitraum) {
    const { von, bis } = zeitraumGrenzen(ekvRequestZeitraum, heute)
    ergebnis = { ...ergebnis, ekvRequestFromDate: von, ekvRequestUntilDate: bis }
  }
  return ergebnis
}

/**
 * Was an das Backend geht: aktuelle Grenzen, ohne die Marken. Das Backend wuerde sie zwar
 * ignorieren (Jackson-Default), aber ein Feld, das kein DTO kennt, gehoert nicht in den Body.
 */
export function fuerBackend<T extends object>(filter: T, heute: Date = new Date()): T {
  const ohneMarken: T & DashboardZeitraeume = { ...mitAktuellenZeitraeumen(filter, heute) }
  delete ohneMarken.createdZeitraum
  delete ohneMarken.ekvRequestZeitraum
  return ohneMarken
}
